import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import {
  mdiHumanMaleChild,
  mdiPassport,
  mdiSnowflakeVariant,
  mdiCalendarRange,
  mdiCow,
  mdiTractorVariant,
  mdiHospitalBox,
  mdiChurch,
  mdiCart,
  mdiHandshake,
  mdiSilverwareForkKnife,
  mdiBookOpenBlankVariant,
  mdiCircle,
  mdiCheckBold,
  mdiSquare,
  mdiStar,
  mdiTriangle,
  mdiCloseCircle,
} from '@mdi/js';
import Select from 'react-select';
import IconOption from './IconOption';
import tailwindTheme from '../../../../../../config/tailwind.config';
import DropdownIcon from './DropdownIcon';
import SingleValue from './SingleValue';

/**
 * Select dropdown component to render multiple options with a single selection.
 * This component render an array of object in form of { name:"", value: "", label: "", icon: "" }.
 * Property `name` must be an string and property `value` can be string, number or object
 */

const SelectIconDropdown = ({
  label = undefined,
  value = undefined,
  setValue,
  isRequired = false,
  id = undefined,
  spaceY = undefined,
  labelTextSize = undefined,
  minHeight = undefined,
  inputHeight = undefined,
  fontSize = undefined,
  menuMinWidth = undefined, // use string to set menu width. like '150px'
  placeholder = undefined,
  inputWidth = undefined, // use tailwind classes to set input width
  isIconAsValue = false, // use this prop to render only the icon as value when  user select an option
  labelMarginBottom = undefined,
  dataTestId = undefined, // Wrapper-level test hook for icon dropdown selectors.
}) => {
  const singleValueWithCustomProps = (props) => (
    <SingleValue {...props} isIconAsValue={isIconAsValue} />
  );
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: minHeight || inputHeight || '2rem',
      height: inputHeight || '2rem',
      borderRadius: '0.375rem',
      fontFamily: 'DM Sans',
      fontWeight: '400',
      borderColor: state.isFocused ? tailwindTheme.theme.extend.colors['vamos-black'] : tailwindTheme.theme.extend.colors['vamos-blue-gray'],
      fontSize,
      backgroundColor: tailwindTheme.theme.extend.colors['vamos-white'],
      boxShadow: state.isFocused ? 'vamos-black' : null,
      '&:hover': {
        borderColor: 'border-vamos-black',
      },
      paddingLeft: '2px',
      paddingRight: '4px',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: tailwindTheme.theme.extend.colors['vamos-black'],
      pointerEvents: 'none',
      ariaHidden: true,
      transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
      padding: 2,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: tailwindTheme.theme.extend.colors['vamos-gray'],
      fontSize: fontSize || 16,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    }),
    indicatorSeparator: () => {},
    clearIndicator: (provided) => ({
      ...provided,
      padding: 0,
      color: tailwindTheme.theme.extend.colors['vamos-blue-gray'],
      '&:hover': {
        color: tailwindTheme.theme.extend.colors['vamos-blue-gray'],
      },
      svg: {
        height: '20px',
        width: '20px',
      },

    }),
    option: () => ({
      display: 'flex',
      padding: '5px',
      marginLeft: '4px',
      gap: '4px',
      '&:hover': {
        backgroundColor: tailwindTheme.theme.extend.colors['vamos-v-light-gray'],
        cursor: 'default',
      },
    }),
    // props for dropdown menu
    menu: (provided) => ({
      ...provided,
      minWidth: menuMinWidth || '100%',
      zIndex: 30,
    }),
  };
  const options = useMemo(() => [
    {
      name: 'No value', value: '', label: 'Select', icon: null,
    },
    {
      name: 'Family', value: 'Family', label: 'Family', icon: mdiHumanMaleChild,
    },
    {
      name: 'H2A', value: 'H2A', label: 'H2A', icon: mdiPassport,
    },
    {
      name: 'Nonseasonal', value: 'Nonseasonal', label: 'Nonseasonal', icon: mdiSnowflakeVariant,
    },
    {
      name: 'Seasonal', value: 'Seasonal', label: 'Seasonal', icon: mdiCalendarRange,
    },
    {
      name: 'Dairy', value: 'Dairy', label: 'Dairy', icon: mdiCow,
    },
    {
      name: 'Worksite', value: 'Worksite', label: 'Worksite', icon: mdiTractorVariant,
    },
    {
      name: 'Clinic', value: 'Clinic', label: 'Clinic', icon: mdiHospitalBox,
    },
    {
      name: 'Church', value: 'Church', label: 'Church', icon: mdiChurch,
    },
    {
      name: 'Grocery Store', value: 'Grocery Store', label: 'Grocery Store', icon: mdiCart,
    },
    {
      name: 'Partner Org', value: 'Partner Organization', label: 'Partner Org', icon: mdiHandshake,
    },
    {
      name: 'Restaurant', value: 'Restaurant', label: 'Restaurant', icon: mdiSilverwareForkKnife,
    },
    {
      name: 'School / Daycare', value: 'School/Daycare', label: 'School / Daycare', icon: mdiBookOpenBlankVariant,
    },
    {
      name: 'Circle', value: 'Circle', label: 'Circle', icon: mdiCircle,
    },
    {
      name: 'Check Mark', value: 'Check Mark', label: 'Check Mark', icon: mdiCheckBold,
    },
    {
      name: 'Square', value: 'Square', label: 'Square', icon: mdiSquare,
    },
    {
      name: 'Star', value: 'Star', label: 'Star', icon: mdiStar,
    },
    {
      name: 'Triangle', value: 'Triangle', label: 'Triangle', icon: mdiTriangle,
    },
    {
      name: 'X', value: 'X', label: 'X', icon: mdiCloseCircle,
    },
  ], []);

  const findOption = React.useCallback((iconValue) => {
    return options.find((o) => o.value === iconValue);
  }, [options]);
  const selectedOption = useMemo(() => findOption(value), [value, findOption]);
  return (
    <div className={`${inputWidth || 'w-full'} ${spaceY || ''}`} data-testid={dataTestId}>
      {/* Exposes a stable container selector for feature specs. */}
      {label && (
      <div className={`${labelMarginBottom} ${labelTextSize || 'text-sm leading-5'} ${isRequired ? 'required' : ''}`}>
        {label}
      </div>
      )}
      <Select
        value={value ? selectedOption : null} // need this condition to show placeholder when option value is ""
        options={options}
        onChange={setValue}
        id={id}
        components={{
          Option: IconOption,
          DropdownIndicator: DropdownIcon,
          SingleValue: singleValueWithCustomProps,
        }}
        styles={customStyles}
        placeholder={placeholder}
        isIconAsValue={isIconAsValue}
        aria-label={label || 'Select an option'}
      />
    </div>
  );
};

SelectIconDropdown.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  setValue: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
  spaceY: PropTypes.string,
  labelTextSize: PropTypes.string,
  minHeight: PropTypes.number,
  inputHeight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  fontSize: PropTypes.string,
  menuMinWidth: PropTypes.string,
  placeholder: PropTypes.string,
  inputWidth: PropTypes.string,
  isIconAsValue: PropTypes.bool,
  labelMarginBottom: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default SelectIconDropdown;
